import { motion } from "framer-motion";
import { ArrowUp, ArrowDown, FileDown, Share2 } from "lucide-react";

export const ease = [0.16, 1, 0.3, 1];

export const FadeIn = ({ children, delay = 0, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.9, delay, ease }}
    className={className}
  >
    {children}
  </motion.div>
);

export const ChapterLabel = ({ no, label }) => (
  <FadeIn className="flex items-center gap-5 mb-10">
    <span className="font-serif-display text-4xl md:text-5xl font-light text-[#d4af37]/80" data-testid={`chapter-no-${no}`}>{no}</span>
    <span className="gold-line w-16" />
    <span className="text-xs md:text-sm tracking-[0.3em] uppercase text-[#a1a3a1]">{label}</span>
  </FadeIn>
);

export const Header = ({ lang, setLang, t, onPdf, onShare, onInvestors }) => (
  <header
    className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-[#0a0c0b]/70 border-b border-white/10"
    data-testid="deck-header"
  >
    <div className="flex items-center justify-between px-6 md:px-12 h-16 md:h-20">
      <a href="#cover" className="flex items-baseline gap-2 group" data-testid="logo-link">
        <span className="font-serif-display text-xl md:text-2xl tracking-wide text-[#f2f0e9]">Golf <span className="text-[#d4af37] italic">&</span> Sea</span>
        <span className="hidden md:inline text-[10px] tracking-[0.3em] uppercase text-[#8c877d]">Is Molas · Sardegna</span>
      </a>
      <div className="flex items-center gap-3 md:gap-6">
        <button
          onClick={onInvestors}
          className="hidden md:block text-xs tracking-[0.2em] uppercase text-[#a1a3a1] hover:text-[#d4af37] transition-colors duration-300"
          data-testid="nav-investors-btn"
        >
          {t.nav.investors}
        </button>
        <button
          onClick={onShare}
          className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-[#a1a3a1] hover:text-[#d4af37] transition-colors duration-300"
          data-testid="share-link-btn"
          title="Share"
        >
          <Share2 size={14} />
        </button>
        <button
          onClick={onPdf}
          className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-[#a1a3a1] hover:text-[#d4af37] transition-colors duration-300"
          data-testid="pdf-export-btn"
        >
          <FileDown size={14} />
          <span className="hidden sm:inline">{t.nav.pdf}</span>
        </button>
        <div className="flex border border-white/15" data-testid="lang-toggle">
          {["it", "en", "ar"].map((l) => (
            <button
              key={l}
              onClick={() => setLang(l)}
              data-testid={`lang-${l}-btn`}
              className={`px-3 py-1.5 text-xs tracking-[0.15em] uppercase transition-colors duration-300 ${
                lang === l ? "bg-[#d4af37] text-[#0a0c0b] font-medium" : "text-[#a1a3a1] hover:text-[#f2f0e9]"
              }`}
            >
              {l}
            </button>
          ))}
        </div>
      </div>
    </div>
  </header>
);

export const BottomNav = ({ index, total, onPrev, onNext }) => (
  <div className="fixed bottom-0 left-0 right-0 z-50 pointer-events-none">
    <div className="flex items-end justify-between px-6 md:px-12 pb-6">
      <div className="pointer-events-auto flex items-baseline gap-2" data-testid="slide-progress">
        <span className="font-serif-display text-3xl text-[#d4af37]">{String(index + 1).padStart(2, "0")}</span>
        <span className="text-xs text-[#8c877d] tracking-[0.2em]">/ {String(total).padStart(2, "0")}</span>
      </div>
      <div className="pointer-events-auto flex gap-2">
        <button
          onClick={onPrev}
          data-testid="nav-prev-btn"
          className="w-11 h-11 md:w-12 md:h-12 border border-white/15 bg-[#0a0c0b]/60 backdrop-blur-md flex items-center justify-center text-[#a1a3a1] hover:text-[#d4af37] hover:border-[#d4af37] transition-colors duration-300"
        >
          <ArrowUp size={16} />
        </button>
        <button
          onClick={onNext}
          data-testid="nav-next-btn"
          className="w-11 h-11 md:w-12 md:h-12 border border-white/15 bg-[#0a0c0b]/60 backdrop-blur-md flex items-center justify-center text-[#a1a3a1] hover:text-[#d4af37] hover:border-[#d4af37] transition-colors duration-300"
        >
          <ArrowDown size={16} />
        </button>
      </div>
    </div>
  </div>
);

export const Marquee = ({ text }) => (
  <div className="border-y border-white/10 py-5 overflow-hidden bg-[#0e100f]" data-testid="editorial-marquee">
    <div className="marquee-track">
      {[0, 1].map((i) => (
        <div key={i} className="flex items-center shrink-0">
          {text.split("·").map((part, j) => (
            <span key={j} className="flex items-center">
              <span className="font-serif-display text-lg md:text-xl italic font-light text-[#8c877d] whitespace-nowrap px-6">{part.trim()}</span>
              <span className="text-[#d4af37] text-[8px]">◆</span>
            </span>
          ))}
        </div>
      ))}
    </div>
  </div>
);
